import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Share2, Video, Camera, Utensils, Download, ExternalLink, Copy, Check, Image, X, Facebook, Instagram } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatWeight, getWeightUnit } from '../utils/weightUnit';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = window.location.origin;

const SHARE_PLATFORMS = [
  { id: 'facebook', label: 'Facebook', color: '#1877F2', icon: Facebook, getUrl: (text, url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}` },
  { id: 'twitter', label: 'X (Twitter)', color: '#000', icon: ExternalLink, getUrl: (text, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
  { id: 'whatsapp', label: 'WhatsApp', color: '#25D366', icon: ExternalLink, getUrl: (text, url) => `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}` },
];

export default function ShareToSocial({ token }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('videos');
  const [videos, setVideos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [meals, setMeals] = useState([]);
  const [shareModal, setShareModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  const { user } = useAuth();
  const system = user?.measurement_system || user?.profile?.measurement_system || 'metric';
  const unit = getWeightUnit(system);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (activeTab === 'videos') fetchVideos();
    if (activeTab === 'photos') fetchPhotos();
    if (activeTab === 'meals') fetchMeals();
  }, [activeTab]);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/video/history`, { headers });
      setVideos((res.data.videos || []).filter(v => v.status === 'completed'));
    } catch { /* ignore */ }
  };

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/progress/photos`, { headers });
      setPhotos(res.data.photos || []);
    } catch { /* ignore */ }
  };

  const fetchMeals = async () => {
    try {
      const res = await axios.get(`${API}/meals/history?limit=10`, { headers });
      setMeals((res.data.meals || []).filter(m => m.image_base64));
    } catch { /* ignore */ }
  };

  const generateShareCard = async (type, item) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    canvas.width = 1080;
    canvas.height = 1080;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, 1080, 1080);
    grad.addColorStop(0, '#FF6600');
    grad.addColorStop(1, '#FF3366');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1080, 1080);

    // Inner card
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    roundRect(ctx, 40, 40, 1000, 1000, 24);
    ctx.fill();

    // Content based on type
    ctx.fillStyle = '#1a1a1a';
    ctx.textAlign = 'center';

    if (type === 'meal') {
      ctx.font = 'bold 48px system-ui';
      ctx.fillText('My Meal Analysis', 540, 120);
      ctx.font = '32px system-ui';
      ctx.fillStyle = '#666';
      ctx.fillText(`${Math.round(item.analysis?.total_calories || 0)} calories`, 540, 180);

      if (item.analysis?.food_items) {
        ctx.textAlign = 'left';
        ctx.font = '28px system-ui';
        item.analysis.food_items.slice(0, 6).forEach((food, i) => {
          ctx.fillStyle = '#333';
          ctx.fillText(`${food.name}`, 100, 260 + i * 50);
          ctx.fillStyle = '#999';
          ctx.fillText(`${Math.round(food.calories)} cal${food.portion_grams ? ` · ${food.portion_grams}g` : ''}`, 100, 290 + i * 50);
        });
      }

      // Macros bar
      const macroY = 600;
      ctx.textAlign = 'center';
      ctx.font = 'bold 36px system-ui';
      ctx.fillStyle = '#FF6600';
      ctx.fillText(`${Math.round(item.analysis?.total_protein || 0)}g`, 270, macroY);
      ctx.fillText(`${Math.round(item.analysis?.total_carbs || 0)}g`, 540, macroY);
      ctx.fillText(`${Math.round(item.analysis?.total_fats || 0)}g`, 810, macroY);
      ctx.font = '24px system-ui';
      ctx.fillStyle = '#999';
      ctx.fillText('Protein', 270, macroY + 35);
      ctx.fillText('Carbs', 540, macroY + 35);
      ctx.fillText('Fats', 810, macroY + 35);

      // Meal tags
      if (item.analysis?.meal_tags?.length) {
        ctx.font = '22px system-ui';
        ctx.fillStyle = '#22c55e';
        ctx.fillText(item.analysis.meal_tags.slice(0, 3).join(' · '), 540, 720);
      }
    } else if (type === 'photo') {
      ctx.font = 'bold 48px system-ui';
      ctx.fillText('My Progress', 540, 120);
      ctx.font = '28px system-ui';
      ctx.fillStyle = '#666';
      ctx.fillText(new Date(item.created_at).toLocaleDateString(), 540, 170);
      if (item.weight) {
        ctx.font = 'bold 72px system-ui';
        ctx.fillStyle = '#FF6600';
        ctx.fillText(`${formatWeight(item.weight, unit)}`, 540, 500);
      }
      if (item.notes) {
        ctx.font = '28px system-ui';
        ctx.fillStyle = '#666';
        ctx.fillText(item.notes.slice(0, 50), 540, 580);
      }
    } else if (type === 'video') {
      ctx.font = 'bold 48px system-ui';
      ctx.fillText('AI Cooking Video', 540, 120);
      ctx.font = '28px system-ui';
      ctx.fillStyle = '#666';
      const promptText = (item.prompt || '').slice(0, 80);
      wrapText(ctx, promptText, 540, 200, 900, 36);
      ctx.font = 'bold 36px system-ui';
      ctx.fillStyle = '#a855f7';
      ctx.fillText('Powered by Sora 2', 540, 500);
    }

    // Watermark
    ctx.fillStyle = '#FF6600';
    ctx.font = 'bold 28px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('Track with NutriFitness AI', 540, 960);
    ctx.font = '20px system-ui';
    ctx.fillStyle = '#999';
    ctx.fillText('nutrifitness.app', 540, 995);

    return canvas.toDataURL('image/png');
  };

  const openShareModal = async (type, item) => {
    const cardImage = await generateShareCard(type, item);
    setShareModal({ type, item, cardImage });
    setCopied(false);
  };

  const shareToNative = async () => {
    if (!shareModal?.cardImage || !navigator.share) return;
    try {
      const blob = await (await fetch(shareModal.cardImage)).blob();
      const file = new File([blob], 'nutrifitness-share.png', { type: 'image/png' });
      await navigator.share({ files: [file], title: 'NutriFitness AI', text: 'Check out my progress on NutriFitness AI!' });
    } catch { /* user cancelled */ }
  };

  const downloadCard = () => {
    if (!shareModal?.cardImage) return;
    const a = document.createElement('a');
    a.href = shareModal.cardImage;
    a.download = 'nutrifitness-share.png';
    a.click();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(APP_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { id: 'videos', label: t('shareSocial.recipeVideos'), icon: Video, count: videos.length },
    { id: 'photos', label: t('shareSocial.progressPhotos'), icon: Camera, count: photos.length },
    { id: 'meals', label: t('shareSocial.mealScans'), icon: Utensils, count: meals.length },
  ];

  return (
    <div className="space-y-4" data-testid="share-to-social">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2.5 bg-gradient-to-br from-[#FF6600] to-pink-500 rounded-xl">
            <Share2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('shareSocial.title')}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('shareSocial.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-[#FF6600] text-white shadow-md'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
            }`}
            data-testid={`share-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700'}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-3">
        {activeTab === 'videos' && (
          videos.length === 0 ? (
            <EmptyState icon={Video} text={t('shareSocial.noVideos')} sub={t('shareSocial.noVideosDesc')} />
          ) : videos.map(vid => (
            <ShareCard key={vid.id} testId={`share-video-${vid.id}`} onShare={() => openShareModal('video', vid)} shareText={t('shareSocial.share')}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Video className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{vid.prompt?.slice(0, 60)}...</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{vid.size} · {vid.duration}s · {new Date(vid.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </ShareCard>
          ))
        )}

        {activeTab === 'photos' && (
          photos.length === 0 ? (
            <EmptyState icon={Camera} text={t('shareSocial.noPhotos')} sub={t('shareSocial.noPhotosDesc')} />
          ) : photos.map((photo, i) => (
            <ShareCard key={photo.id || i} testId={`share-photo-${i}`} onShare={() => openShareModal('photo', photo)} shareText={t('shareSocial.share')}>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0">
                  {photo.image_url ? (
                    <img src={photo.image_url} alt="Progress" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Camera className="w-6 h-6 text-gray-400" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{photo.weight ? formatWeight(photo.weight, unit) : 'Progress Photo'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(photo.created_at).toLocaleDateString()}{photo.notes ? ` · ${photo.notes.slice(0, 30)}` : ''}</p>
                </div>
              </div>
            </ShareCard>
          ))
        )}

        {activeTab === 'meals' && (
          meals.length === 0 ? (
            <EmptyState icon={Utensils} text={t('shareSocial.noMeals')} sub={t('shareSocial.noMealsDesc')} />
          ) : meals.map((meal, i) => (
            <ShareCard key={meal.id || i} testId={`share-meal-${i}`} onShare={() => openShareModal('meal', meal)} shareText={t('shareSocial.share')}>
              <div className="flex items-center gap-3">
                <img src={meal.image_base64} alt="Meal" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{meal.meal_type}</p>
                    {meal.analysis?.dish_count > 1 && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{meal.analysis.dish_count} dishes</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {Math.round(meal.analysis?.total_calories || 0)} cal · {new Date(meal.timestamp).toLocaleDateString()}
                  </p>
                  {meal.analysis?.meal_tags?.length > 0 && (
                    <div className="flex gap-1 mt-0.5">
                      {meal.analysis.meal_tags.slice(0, 2).map((tag, j) => (
                        <span key={j} className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded-full">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </ShareCard>
          ))
        )}
      </div>

      {/* Share Modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setShareModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Preview */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900 dark:text-white">{t('shareSocial.title')}</h3>
                <button onClick={() => setShareModal(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" data-testid="close-share-modal">
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              {shareModal.cardImage && (
                <img src={shareModal.cardImage} alt="Share card" className="w-full max-h-[280px] object-contain rounded-xl border border-gray-200 dark:border-gray-700" data-testid="share-card-preview" />
              )}
            </div>

            {/* Share Options */}
            <div className="px-4 pb-4 space-y-3">
              {/* Platform buttons */}
              <div className="grid grid-cols-3 gap-2">
                {SHARE_PLATFORMS.map(p => (
                  <a
                    key={p.id}
                    href={p.getUrl('Check out my progress on NutriFitness AI!', APP_URL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
                    data-testid={`share-${p.id}`}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: p.color }}>
                      <p.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-300">{p.label}</span>
                  </a>
                ))}
              </div>

              {/* Native share (mobile) */}
              {navigator.share && (
                <button
                  onClick={shareToNative}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#FF6600] hover:bg-[#E55C00] text-white rounded-xl font-medium text-sm transition-colors"
                  data-testid="native-share-btn"
                >
                  <Share2 className="w-4 h-4" /> {t('shareSocial.shareViaDevice')}
                </button>
              )}

              {/* Download + Copy */}
              <div className="flex gap-2">
                <button
                  onClick={downloadCard}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
                  data-testid="download-share-card"
                >
                  <Download className="w-4 h-4" /> {t('shareSocial.saveImage')}
                </button>
                <button
                  onClick={copyLink}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl text-sm hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? t('shareSocial.copied') : t('shareSocial.copyLink')}
                </button>
              </div>

              <p className="text-[11px] text-gray-400 dark:text-gray-500 dark:text-gray-400 text-center">
                {t('shareSocial.uploadTip')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareCard({ children, onShare, testId, shareText }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between gap-3" data-testid={testId}>
      <div className="flex-1 min-w-0">{children}</div>
      <button
        onClick={onShare}
        className="flex items-center gap-1.5 px-3 py-2 bg-[#FF6600] hover:bg-[#E55C00] text-white text-xs font-medium rounded-lg transition-colors flex-shrink-0"
        data-testid={`${testId}-btn`}
      >
        <Share2 className="w-3.5 h-3.5" /> {shareText}
      </button>
    </div>
  );
}

function EmptyState({ icon: Icon, text, sub }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-100 dark:border-gray-700 text-center" data-testid="share-empty-state">
      <Icon className="w-10 h-10 text-gray-300 dark:text-gray-600 dark:text-gray-300 mx-auto mb-3" />
      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{text}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400 mt-1">{sub}</p>
    </div>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let curY = y;
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line.trim(), x, curY);
      line = word + ' ';
      curY += lineHeight;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, curY);
}
